import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Form,
} from "@/components/ui/form";
import {
  useGetPaymentLinkQuery,
  useProcessPaymentMutation,
} from "../../store/customer_payments/apis";
import GetCard from "../Moves/components/GetCard";
import { useEffect, useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { CreditCard, PlusCircle } from "lucide-react";
import { cn, formatDateWithOrdinal, showCardDetails } from "@/lib/utils";
import { parseISO } from "date-fns";
import { PAYMENT_PURPOSE_TYPES } from "@/constants";
import { PaymentIntent } from "@/types";
import AuthorizeNetCard from "../Payments/authorize-net/AuthorizeNetCard";
import { faExclamationTriangle } from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type BillingInfo = {
  name?: string;
  address?: string;
  zip?: string;
  country?: string;
};

type FormData = {
  payment_method_id?: number;
  payment_method_token?: string;
  billing_info: BillingInfo;
};

const slideButtonStyles = `
  .slide-button-container {
    position: relative;
    width: 100%;
    height: 48px;
    border-radius: 24px;
    overflow: hidden;
  }

  .slide-button {
    -webkit-appearance: none;
    width: 100%;
    height: 48px;
    background: transparent;
    outline: none;
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    z-index: 2;
  }

  .slide-button::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 48px;
    height: 48px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    cursor: grab;
  }

  .slide-button-background {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: #16a34a;
    transition: width 0.1s;
    z-index: 1;
  }

  .slide-button-text {
    position: absolute;
    width: 100%;
    text-align: center;
    line-height: 48px;
    color: #000000;
    z-index: 1;
    font-weight: 600;
    pointer-events: none;
  }
`;

const SlideToPayButton = ({
  ready = false,
  onSubmit,
  isProcessing,
  amount,
  shouldReset = false,
}: {
  ready?: boolean;
  onSubmit: () => void;
  isProcessing: boolean;
  amount: number;
  shouldReset?: boolean;
}) => {
  const [slideValue, setSlideValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (shouldReset) {
      setSlideValue(0);
    }
  }, [shouldReset]);

  const handleSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setSlideValue(value);

    if (value === 100) {
      onSubmit();
    }
  };

  const formattedAmount = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);

  return (
    <div
      className={`slide-button-container font-semibold ${
        isProcessing || !ready
          ? "bg-stone-500 opacity-50 cursor-not-allowed"
          : "bg-yellow-500"
      }`}
    >
      <style>{slideButtonStyles}</style>
      <div
        className="slide-button-background"
        style={{ width: `${slideValue}%` }}
      />
      <input
        type="range"
        min="0"
        max="100"
        value={slideValue}
        className="slide-button"
        onChange={handleSlide}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => {
          setIsDragging(false);
          if (slideValue < 100) {
            setSlideValue(0);
          }
        }}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => {
          setIsDragging(false);
          if (slideValue < 100) {
            setSlideValue(0);
          }
        }}
        disabled={isProcessing || !ready}
      />
      <div
        className={cn(
          "slide-button-text font-semibold text-base uppercase font-mono"
        )}
      >
        {isProcessing
          ? "Processing..."
          : isDragging
          ? `Pay ${formattedAmount}`
          : "Slide to Pay"}
      </div>
    </div>
  );
};

const CustomerPaymentLinks = () => {
  const { digest } = useParams();
  const { data: paymentData, isLoading: isLoadingPaymentData } =
    useGetPaymentLinkQuery(digest || "", { skip: !digest });
  const [processPayment, { isLoading: isProcessing }] =
    useProcessPaymentMutation();
  const form = useForm<FormData>();
  const [resetSlider, setResetSlider] = useState(false);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!paymentData) return;

    if (paymentData.payment_methods.length === 0) {
      showCardInput();
    }
  }, [paymentData]);

  function showCardInput() {
    const elem = document.getElementById("new-card-input");
    if (elem) {
      elem.classList.remove("hidden");
    }
  }

  const onSubmit = async () => {
    if (!paymentData?.mover) return;
    if (!digest) return;
    if (
      !paymentIntent &&
      paymentData.mover.payment_configuration.processor === "fiserv_fwd"
    )
      return;
    if (
      !form.getValues("payment_method_id") &&
      !form.getValues("payment_method_token")
    )
      return;

    try {
      await processPayment({
        digest,
        payment_intent_id: paymentIntent?.id || "",
        payment_method_id: form.getValues("payment_method_id"),
        payment_method_token: form.getValues("payment_method_token"),
        bill_to: form.getValues("billing_info"),
        mover_id: paymentData?.mover.id,
      }).unwrap();

      setPaymentProcessed(true);
    } catch (error) {
      console.error(error);
      setError("Your payment failed. Please try again with a different card.");
      setResetSlider(true);
    }
  };

  function handleToken(token: string) {
    console.debug("card nonce set", token);
    form.setValue("payment_method_token", token);
  }

  function setBillingInfo(billingInfo: BillingInfo) {
    form.setValue("billing_info", billingInfo);
  }

  function handlePaymentIntent(pi: PaymentIntent) {
    setPaymentIntent(pi);
  }

  if (isLoadingPaymentData) return null;
  if (!paymentData) {
    return (
      <div className="h-screen max-w-xl mx-auto flex flex-col justify-center items-center py-4 px-12 text-xl font-medium">
        This payment has either been made or the link is no longer valid.
      </div>
    );
  }

  if (paymentProcessed) {
    return (
      <div className="max-w-xl mx-auto flex flex-col justify-start py-4 px-16">
        <div className="mt-4 text-lg font-normal">
          Thank you!
          <br />
          <br />
          Your payment has been successfully processed and applied to your move.
        </div>
        <div className="mt-8 font-mono max-w-md mx-auto bg-white px-8 py-4 border border-gray-200 shadow-sm">
          <div className="text-center mb-4">
            <div className="flex flex-col items-center justify-center gap-2 border-b border-stone-700 pb-4 mb-4">
              <img
                src={paymentData.mover.logo_url}
                alt={paymentData.mover.name}
                className="max-h-[60px]"
              />
              <div className="text-lg font-semibold">
                {paymentData.mover.name}
              </div>
            </div>
            <div className="text-lg font-bold mb-1">RECEIPT</div>
            <div className="text-sm text-gray-600">
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <div>Customer:</div>
              <div>{paymentData.move.customer_name}</div>
            </div>

            <div className="flex justify-between">
              <div>Service Date:</div>
              <div>
                {paymentData.move.service_date
                  ? formatDateWithOrdinal(
                      parseISO(paymentData.move.service_date)
                    )
                  : "-"}
              </div>
            </div>

            {/* <div className="border-t border-gray-300 my-4"></div> */}

            <div className="flex justify-between font-bold">
              <div>Amount Paid:</div>
              <div>
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
                }).format(paymentData.payment_link.amount)}
              </div>
            </div>

            <div className="border-t border-gray-300 my-4"></div>

            <div className="text-center text-xs text-gray-500">
              Thank you for your business!
            </div>
          </div>
          <div className="mt-4 text-base font-normal">
            If you have any questions, please contact us at{" "}
            <a href={`mailto:${paymentData.mover.email}`}>
              {paymentData.mover.email}
            </a>{" "}
            or call {paymentData.mover.business_phone}.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#FFFFFF",
        minWidth: "100vw",
        minHeight: "100vh",
        padding: "16px 16px",
      }}
    >
      <div className="flex flex-col items-center gap-6 max-w-2xl mx-auto container pb-4 border-b border-stone-200">
        <div className="w-full flex flex-row justify-between items-center gap-0.5 border-b border-stone-200 pb-4">
          <img
            src={paymentData.mover.logo_url}
            alt={paymentData.mover.name}
            className="max-h-[60px]"
          />
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-base font-semibold">
              {paymentData.mover.name}
            </span>
            <span className="text-sm text-gray-800">
              {paymentData.mover.email && (
                <a href={`mailto:${paymentData.mover.email}`}>
                  {paymentData.mover.email}
                </a>
              )}
            </span>
            <span className="text-sm text-gray-800">
              {paymentData.mover.business_phone && (
                <a href={`tel:${paymentData.mover.business_phone}`}>
                  {paymentData.mover.business_phone}
                </a>
              )}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{ backgroundColor: "#F6F5F2" }}
        className="max-w-2xl mx-auto flex flex-col justify-start pt-2 pb-4 px-6"
      >
        <h1 className="text-lg font-semibold text-center">Secure Pay</h1>
        <div className="my-4">
          <div className="flex flex-row gap-2 items-center justify-center text-center text-base">
            {/* <Label>Name:</Label> */}
            <div className="font-semibold text-gray-800">
              {paymentData.move.customer_name}
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center justify-center text-center text-base">
            <div className="text-sm text-gray-800">
              Job #{paymentData.move.number}
            </div>
          </div>
          <div className="hidden flex flex-row gap-2 items-center justify-center text-center text-base">
            <Label>Service Date:</Label>
            <div className="font-medium text-gray-800">
              {paymentData.move.service_date
                ? formatDateWithOrdinal(parseISO(paymentData.move.service_date))
                : "-"}
            </div>
          </div>
          <div className="my-4 flex flex-col items-center gap-0.5">
            <div className="text-2xl font-semibold">
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              }).format(paymentData.payment_link.amount)}
            </div>
            <Label className="text-sm font-medium bg-muted px-2 py-1 rounded-md text-black">
              {
                PAYMENT_PURPOSE_TYPES[
                  paymentData.payment_link
                    .purpose as keyof typeof PAYMENT_PURPOSE_TYPES
                ]
              }
            </Label>

            {paymentData.payment_link.processing_fee_amount > 0 && (
              <div className="text-xs font-mono px-2 text-stone-700">
                {Intl.NumberFormat("en-US", {
                  style: "percent",
                  minimumFractionDigits: 2,
                }).format(
                  paymentData.payment_link.processing_fee_percentage / 100
                )}{" "}
                card processing fee included
              </div>
            )}
          </div>

          {error && (
            <div className="my-3 text-red-600 text-sm font-medium flex flex-row justify-center items-center gap-1">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-4 hidden">
            <div className="text-sm font-medium text-muted-foreground mb-2">
              How would you like to pay?
            </div>
            <RadioGroup defaultValue="credit" className="flex flex-col gap-2">
              <div className="w-1/2">
                <RadioGroupItem
                  value="credit"
                  id="card-credit"
                  className="peer sr-only"
                />
                <Label
                  htmlFor="card-credit"
                  className="flex flex-row items-baseline gap-2 rounded-lg border border-stone-200 bg-popover px-4 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary peer-data-[state=checked]:bg-amber-50 [&:has([data-state=checked])]:bg-amber-50 cursor-pointer"
                >
                  <div className="flex flex-row items-center gap-1">
                    <CreditCard className="h-4 w-4" />
                    <div className="font-semibold">Credit Card</div>
                  </div>
                </Label>
              </div>

              <div className="w-1/2">
                <RadioGroupItem
                  value="debit"
                  id="card-debit"
                  className="peer sr-only"
                />
                <Label
                  htmlFor="card-debit"
                  className="flex flex-row items-baseline gap-2 rounded-lg border border-stone-200 bg-popover px-4 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary peer-data-[state=checked]:bg-amber-50 [&:has([data-state=checked])]:bg-amber-50 cursor-pointer"
                >
                  <div className="flex flex-row items-center gap-1">
                    <CreditCard className="h-4 w-4" />
                    <div className="font-semibold">Debit Card</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col"
            >
              <FormField
                control={form.control}
                name="payment_method_id"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        className="grid gap-3"
                      >
                        {paymentData.payment_methods.map((pm) => (
                          <div key={pm.id}>
                            <RadioGroupItem
                              value={pm.id.toString()}
                              id={pm.id.toString()}
                              className="peer sr-only"
                            />
                            <Label
                              htmlFor={pm.id.toString()}
                              className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                            >
                              <CreditCard className="h-4 w-4" />
                              <div className="flex-1 font-semibold">
                                {showCardDetails(pm)}{" "}
                                <span className="text-xs text-gray-800 font-normal">
                                  (expires {pm.exp_month}/{pm.exp_year})
                                </span>
                              </div>
                            </Label>
                          </div>
                        ))}

                        {paymentData.payment_methods &&
                          paymentData.payment_methods.length > 0 && (
                            <div>
                              <Label
                                htmlFor="new"
                                className="flex flex-row items-center gap-4 rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                              >
                                <PlusCircle className="h-4 w-4" />
                                <div
                                  className="flex-1"
                                  onClick={() => showCardInput()}
                                >
                                  New Card
                                </div>
                              </Label>
                            </div>
                          )}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div id="new-card-input" className="hidden mt-3">
                {/* <div className="text-sm font-medium text-muted-foreground mb-2">
                  Enter your card details
                </div> */}

                {paymentData.mover.payment_configuration.processor ===
                  "remedy_authorize" && (
                  <AuthorizeNetCard
                    handleToken={handleToken}
                    handleBillingInfo={setBillingInfo}
                    authorizeLoginId={
                      paymentData.mover.payment_configuration.authorize_login_id
                    }
                    authorizeClientKey={
                      paymentData.mover.payment_configuration
                        .authorize_client_key
                    }
                  />
                )}
                {paymentData.mover.payment_configuration.processor ===
                  "fiserv_fwd" && (
                  <GetCard
                    amount={paymentData.payment_link.amount}
                    handleToken={handleToken}
                    handlePaymentIntent={handlePaymentIntent}
                    moverId={paymentData.mover.id}
                  />
                )}
              </div>

              <div className="mt-6 space-y-2">
                <h3 className="text-center text-base font-normal font-paper">
                  Thank you for taking care of this!
                </h3>
                <SlideToPayButton
                  ready={Boolean(
                    (form.watch("payment_method_id") &&
                      String(form.watch("payment_method_id")).length > 0) ||
                      (form.watch("payment_method_token") &&
                        String(form.watch("payment_method_token")).length > 0)
                  )}
                  onSubmit={form.handleSubmit(onSubmit)}
                  isProcessing={isProcessing}
                  amount={paymentData.payment_link.amount}
                  shouldReset={resetSlider}
                />
              </div>

              <div className="mt-3 text-xs font-normal font-mono text-center">
                A receipt will be sent to your email on record.
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CustomerPaymentLinks;
